// @flow
import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  makeStyles,
  Snackbar,
  Typography,
} from '@material-ui/core';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import {Controller, useForm} from 'react-hook-form';
import Alert from '@material-ui/lab/Alert';
import {Link, useHistory, useParams} from 'react-router-dom';
import CustomExitPrompt from '../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {
  ArrowBack,
  DeleteOutlined,
  ExpandMore,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import DeleteConfirmationModal from '../../../products/components/DeleteConfirmationModal/DeleteConfirmationModal';

import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';
import TelInput from '../../../../elements/TelInput/TelInput';
import {useIsFeatureInTier} from '../../../../utils/component';
import {COLLABORATOR_FEATURE_GROUPS} from '../api';
import {STORE_FEATURES_INFO} from '../../../../constants/store-features';

import type {Store, Collaborator} from '../../../../entities';
import type {SaveCollaboratorRequest} from '../api';
import type {AccountsConfigAction} from '../duck';

type Props = {|
  store?: Store,
  collaborator?: Collaborator,
  fetchCollaborator: (payload: AccountsConfigAction) => void,
  createCollaborator: (payload: AccountsConfigAction) => void,
  updateCollaborator: (payload: AccountsConfigAction) => void,
  deleteCollaborator: (payload: AccountsConfigAction) => void,
  isFetching?: boolean,
  fetchError?: boolean,
  isSaving?: boolean,
  savingSuccess?: boolean,
  saveError?: boolean,

  resetState: () => void,
|};

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: '12px',
    padding: theme.spacing(2),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
    },
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  subtitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 600,
  },

  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  textField: {},
  greyText: {
    marginBottom: theme.spacing(2),
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  bold: {
    fontWeight: 600,
  },
  section: {
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    padding: theme.spacing(1, 2, 2, 2),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
    },
  },
  switchesContainer: {
    width: '100%',
    backgroundColor: colors.grey200,
    borderRadius: '8px',
    overflow: 'hidden',
    // border: `1px solid ${colors.grey300}`,
  },
  arrowTooltip: {
    borderRadius: '6px',
    background: colors.grey300,
    color: colors.grey800,
  },
  arrowTooltipArrow: {
    color: colors.grey300,
  },
  switch: {
    border: `1px solid ${colors.grey300}`,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    // justifyContent: 'space-between',
  },
  switchLabel: {
    fontWeight: 500,
    fontSize: '0.875rem',
    fontColor: colors.grey800,
  },
  switchContainer: {
    marginLeft: 'auto',
  },
  bottomMargin: {
    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(6),
    },
  },
  bottomBar: {
    zIndex: 1200, // same as sidebar
    position: 'fixed',
    bottom: 0,
    marginLeft: theme.spacing(-4),
    width: 'calc(100% - 264px)',
    backgroundColor: 'white',
    boxShadow: '0px -4px 12px rgba(222, 222, 222, 0.25)',
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
      width: '100%',
    },
  },
  bottomBarContent: {
    maxWidth: 1300,
    margin: theme.spacing(0, 4),
    padding: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bottomBarText: {
    fontWeight: 600,
  },
  bottomBarButton: {
    height: 48,
    fontSize: '1.125rem',
  },
  saveButton: {
    // marginBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
  deleteButton: {
    color: colors.error,
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2, 'auto'),
    },
  },
  alert: {
    width: '100%',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
  accordion: {
    boxShadow: 'none',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '12px !important',
    marginBottom: theme.spacing(1.5),
    backgroundColor: colors.white,
    width: '100%',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: `0 0 ${theme.spacing(1.5)}px 0`,
      // boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
    },
  },
  accordionSummary: {
    minHeight: 64,
    padding: theme.spacing(0, 2),
    '&.Mui-expanded': {
      minHeight: 64,
      borderBottom: `1px solid ${colors.grey200}`,
    },
  },
  accordionSummaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginRight: theme.spacing(1),
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: colors.grey900,
  },
  accordionDescription: {
    fontSize: '0.8125rem',
    color: colors.grey600,
    marginTop: 2,
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(1, 2, 2, 2),
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 1),
    borderRadius: 8,
    marginBottom: theme.spacing(0.5),
    transition: 'background-color 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.grey100,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  featureItemInfo: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  featureItemName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey800,
  },
  featureItemDescription: {
    fontSize: '0.75rem',
    color: colors.grey500,
    marginTop: 2,
  },
  selectAllContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  selectAllLabel: {
    fontSize: '0.8125rem',
    color: colors.grey600,
    fontWeight: 500,
  },
}));

export default function CollaboratorEdit(props: Props) {
  const {
    collaborator,
    fetchCollaborator,
    createCollaborator,
    updateCollaborator,
    isFetching,
    fetchError,
    isSaving,
    savingSuccess,
    saveError,
  } = props;

  const classes = useStyles();
  const history = useHistory();

  const hasCollaboratorsFeature = useIsFeatureInTier('STORE_COLLABORATOR');

  useEffect(() => {
    if (!hasCollaboratorsFeature) history.replace('/configure/accounts');
  }, [hasCollaboratorsFeature, history]);

  const {collaboratorId} = useParams();

  useEffect(() => {
    if (collaboratorId && collaboratorId !== 'new')
      fetchCollaborator({collaboratorId});
  }, [fetchCollaborator, collaboratorId]);

  const {register, handleSubmit, errors, watch, formState, control} = useForm();
  const {isDirty} = formState;

  const [selectedFeatures, setSelectedFeatures] = useState([]);

  const isItemChecked = useCallback(
    (features: string[]) => features.every(f => selectedFeatures.includes(f)),
    [selectedFeatures]
  );

  const isGroupAllChecked = useCallback(
    (groupItems: Array<{features: string[], ...}>) =>
      groupItems.every(item => isItemChecked(item.features)),
    [isItemChecked]
  );

  const isGroupIndeterminate = useCallback(
    (groupItems: Array<{features: string[], ...}>) => {
      const someChecked = groupItems.some(item => isItemChecked(item.features));
      const allChecked = groupItems.every(item => isItemChecked(item.features));
      return someChecked && !allChecked;
    },
    [isItemChecked]
  );

  const toggleFeatureItem = useCallback((features: string[]) => {
    setSelectedFeatures(prev => {
      const allOn = features.every(f => prev.includes(f));
      if (allOn) {
        return prev.filter(f => !features.includes(f));
      }
      const newFeatures = [...prev];
      features.forEach(f => {
        if (!newFeatures.includes(f)) newFeatures.push(f);
      });
      return newFeatures;
    });
  }, []);

  const toggleGroupAll = useCallback(
    (groupItems: Array<{features: string[], ...}>) => {
      const allFeatures = groupItems.flatMap(item => item.features);
      setSelectedFeatures(prev => {
        const allOn = allFeatures.every(f => prev.includes(f));
        if (allOn) {
          return prev.filter(f => !allFeatures.includes(f));
        }
        const newFeatures = [...prev];
        allFeatures.forEach(f => {
          if (!newFeatures.includes(f)) newFeatures.push(f);
        });
        return newFeatures;
      });
    },
    []
  );

  useEffect(() => {
    if (!collaborator) return;
    const selectedFeaturesNames = collaborator.collaboratorStoreFeatures.map(
      ({name}) => name
    );
    setSelectedFeatures(selectedFeaturesNames);
  }, [collaborator]);

  const handleFormSubmit = data => {
    const collaboratorRequest: SaveCollaboratorRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      password: data.assignPassword ? data.assignPassword : undefined,
      storeFeatures: selectedFeatures,
    };
    // console.log(collaboratorRequest);
    if (collaborator)
      updateCollaborator({id: collaborator.id, collaboratorRequest});
    else {
      createCollaborator({collaboratorRequest});
    }
  };

  // const [alertOpen, setAlertOpen] = useState(false);
  // const [error, setError] = useState(false);

  useEffect(() => {
    if (savingSuccess) history.push('/configure/accounts/collaborators');
  }, [history, savingSuccess]);

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const [promptOpen, setPromptOpen] = useState(false);
  const handlePromptOpen = () => setPromptOpen(true);
  const handlePromptClose = () => setPromptOpen(false);
  const handleDeleteClick = () => {
    // if (collaborator) deleteCollaborator({id: collaborator.id});
    if (collaborator)
      updateCollaborator({
        id: collaborator.id,
        collaboratorRequest: {archived: true},
      });
  };

  return (
    <>
      <div className={classes.root}>
        <CustomExitPrompt when={isDirty && !savingSuccess} />
        <Snackbar
          open={!!saveError || !!fetchError}
          autoHideDuration={4000}
          // onClose={() => setError(false)}
        >
          <Alert
            variant="filled"
            severity="error"
            // onClose={() => setError(false)}
          >
            {saveError
              ? 'Error al guardar colaborador'
              : fetchError
              ? 'Error'
              : ''}
          </Alert>
        </Snackbar>
        <div className={classes.subRouteHeader}>
          <IconButton
            variant="contained"
            size="small"
            component={Link}
            to="/configure/accounts/collaborators"
          >
            <ArrowBack />
          </IconButton>
          <Typography className={classes.subRouteTitle}>
            {collaboratorId === 'new'
              ? 'Agregar colaborador'
              : 'Editar colaborador'}
          </Typography>
        </div>
        <Divider className={classes.divider} />
        <>
          <Typography className={classes.subtitle}>
            Información Básica
          </Typography>
          {!isFetching &&
            (collaborator || (!collaborator && collaboratorId === 'new')) && (
              <Grid spacing={2} container>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    className={classes.textField}
                    label="Nombre"
                    id="firstName"
                    name="firstName"
                    defaultValue={collaborator ? collaborator.firstName : ''}
                    error={!!errors.firstName}
                    helperText={
                      errors.firstName ? errors.firstName.message : ''
                    }
                    inputRef={register({
                      required: 'Campo obligatorio',
                    })}
                    type="text"
                    disabled={isSaving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    className={classes.textField}
                    label="Apellido"
                    id="lastName"
                    name="lastName"
                    defaultValue={collaborator ? collaborator.lastName : ''}
                    error={!!errors.lastName}
                    helperText={errors.lastName ? errors.lastName.message : ''}
                    inputRef={register({
                      required: 'Campo obligatorio',
                    })}
                    type="text"
                    disabled={isSaving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    className={classes.textField}
                    label="Correo"
                    name="email"
                    autoComplete="off"
                    inputProps={{
                      autoComplete: 'off',
                    }}
                    defaultValue={collaborator ? collaborator.email : ''}
                    error={!!errors.email}
                    helperText={errors.email ? errors.email.message : ''}
                    inputRef={register({
                      required: 'Campo obligatorio',
                    })}
                    type="text"
                    disabled={isSaving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="phone"
                    autoComplete="off"
                    inputProps={{
                      autoComplete: 'off',
                    }}
                    defaultValue={collaborator ? collaborator.phone : ''}
                    render={({onChange, value}) => (
                      <TelInput
                        label={'Whatsapp'}
                        fullWidth
                        defaultValue={collaborator ? collaborator.phone : ''}
                        name="phone"
                        disabled={isSaving}
                        onChange={v => {
                          onChange(v);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    className={classes.textField}
                    label="Asignar Contraseña"
                    autoComplete="new-password"
                    inputProps={{
                      autoComplete: 'new-password',
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    name="assignPassword"
                    // defaultValue={collaborator ? collaborator.password : ''}
                    error={!!errors.password}
                    helperText={errors.password ? errors.password.message : ''}
                    inputRef={register({
                      required: collaborator ? undefined : 'Campo obligatorio',
                      // validate: (val: string) => {
                      //   console.log('password validate');
                      //   if (watch('confirmPassword') !== val) {
                      //     return 'Your passwords do no match';
                      //   }
                      // },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    disabled={isSaving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    className={classes.textField}
                    label="Confirmar contraseña"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    name="confirmPassword"
                    // defaultValue={collaborator ? collaborator.password : ''}
                    error={!!errors.confirmPassword}
                    helperText={
                      errors.confirmPassword
                        ? errors.confirmPassword.message
                        : ''
                    }
                    inputRef={register({
                      required: collaborator ? undefined : 'Campo obligatorio',
                      validate: (val: string) =>
                        val === watch('assignPassword') ||
                        'Las contraseñas no coinciden',
                    })}
                    type={showPassword ? 'text' : 'password'}
                    disabled={isSaving}
                  />
                </Grid>
              </Grid>
            )}
          {collaborator && (
            <DeleteConfirmationModal
              title="¿Seguro que quieres eliminar este colaborador?"
              content="Si lo eliminas no lo podrás recuperar."
              open={promptOpen}
              handleClose={handlePromptClose}
              handleDeleteClick={handleDeleteClick}
            />
          )}
        </>
      </div>
      <div className={classes.section}>
        <Typography className={classes.subtitle}>Permisos</Typography>
        <Typography className={classes.greyText}>
          Habilita o deshabilita los permisos que tu colaborador tendrá dentro
          de cada módulo.
        </Typography>
        <Divider className={classes.divider} />
        {COLLABORATOR_FEATURE_GROUPS.map(group => {
          const groupAllChecked = isGroupAllChecked(group.items);
          const groupIndeterminate = isGroupIndeterminate(group.items);
          const getFeatureInfo = (featureId: string) => {
            const info = STORE_FEATURES_INFO[featureId];
            return info || {name: featureId, description: ''};
          };
          const getItemDisplayName = (item: {
            id: string,
            name?: string,
            features: string[],
            ...
          }) => {
            if (item.name) return item.name;
            if (item.features.length === 1) {
              return getFeatureInfo(item.features[0]).name;
            }
            return getFeatureInfo(item.id).name || item.id;
          };
          const getItemDescription = (item: {
            id: string,
            name?: string,
            features: string[],
            ...
          }) => {
            if (item.features.length === 1) {
              return getFeatureInfo(item.features[0]).description;
            }
            return item.features.map(f => getFeatureInfo(f).name).join(', ');
          };

          return (
            <Accordion key={group.id} className={classes.accordion}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                className={classes.accordionSummary}
              >
                <div className={classes.accordionSummaryContent}>
                  <div>
                    <Typography className={classes.accordionTitle}>
                      {group.name}
                    </Typography>
                    {group.description && (
                      <Typography className={classes.accordionDescription}>
                        {group.description}
                      </Typography>
                    )}
                  </div>
                  <label
                    className={classes.selectAllContainer}
                    onClick={e => e.stopPropagation()}
                  >
                    <Typography className={classes.selectAllLabel}>
                      Todos
                    </Typography>
                    <Checkbox
                      color="primary"
                      size="small"
                      indeterminate={groupIndeterminate}
                      checked={groupAllChecked}
                      onChange={() => toggleGroupAll(group.items)}
                    />
                  </label>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                {group.items.map(item => (
                  <label key={item.id} className={classes.featureItem}>
                    <div className={classes.featureItemInfo}>
                      <Typography className={classes.featureItemName}>
                        {getItemDisplayName(item)}
                      </Typography>
                      <Typography className={classes.featureItemDescription}>
                        {getItemDescription(item)}
                      </Typography>
                    </div>
                    <IosSwitch
                      checked={isItemChecked(item.features)}
                      onChange={() => toggleFeatureItem(item.features)}
                    />
                  </label>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>
      <div className={classes.bottomMargin} />
      <Hidden xsDown>
        <div className={classes.bottomBar}>
          <div className={classes.bottomBarContent}>
            <CustomButton
              className={classes.deleteButton}
              startIcon={<DeleteOutlined />}
              onClick={handlePromptOpen}
            >
              Eliminar Colaborador
            </CustomButton>
            <CustomButton
              variant="contained"
              className={classes.bottomBarButton}
              onClick={handleSubmit(handleFormSubmit)}
              disabled={
                isSaving
                // disableButton
              }
            >
              Guardar
            </CustomButton>
          </div>
        </div>
      </Hidden>
      <Hidden smUp>
        <div className={classes.row}>
          <CustomButton
            className={classes.deleteButton}
            startIcon={<DeleteOutlined />}
            onClick={handlePromptOpen}
          >
            Eliminar Colaborador
          </CustomButton>
          <CustomButton
            variant="contained"
            color="primary"
            className={classes.saveButton}
            onClick={handleSubmit(handleFormSubmit)}
          >
            Guardar cambios
          </CustomButton>
        </div>
      </Hidden>
    </>
  );
}

CollaboratorEdit.propTypes = {
  collaborator: PropTypes.object,
  resetState: PropTypes.func.isRequired,
};
